/**
 * city/vegetation/trees-tropical.js
 * Cây bóng mát nhiệt đới đặc thù đô thị Việt Nam: Phượng vĩ, bàng, dừa, hoa sữa, bằng lăng
 */

import * as THREE from 'three';
import { createRng } from '../builder-core.js';

class MethodsHolder {
  // PHƯỢNG VĨ: thân ngắn chia 3 cành xoè, tán rộng dẹt như chiếc ô, hoa đỏ cam rực phủ ngọn
  createPoincianaTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_PhuongVi_Voxel';
    const rnd = createRng(options.seed ?? 11);
    this._treePit(tree, 1.4, rnd);
    const top = this._trunk(tree, {
      height: 2.3,
      baseW: 0.36,
      topW: 0.26,
      segments: 4,
      lean: [0.06, 0],
      color: '#5b4636'
    });
    const bark = this.mat('#5b4636', { roughness: 0.95 });
    [
      [1.0, 0.7, 0.3],
      [-0.9, 0.6, -0.4],
      [0.1, 0.7, 1.0],
      [-0.2, 0.75, -0.9]
    ].forEach(([dx, dy, dz]) => {
      this._limb(tree, top, new THREE.Vector3(top.x + dx, top.y + dy, top.z + dz), 0.16, bark);
    });
    this._canopy(
      tree,
      {
        x: top.x,
        y: top.y + 1.0,
        z: top.z,
        rx: 2.1,
        ry: 0.65,
        rz: 2.0,
        palette: ['#3f6212', '#4d7c0f', '#65a30d', '#365314'],
        count: 75,
        accent: ['#dc2626', '#ef4444', '#f97316', '#b91c1c'],
        accentRatio: 0.45
      },
      rnd
    );
    return tree;
  }

  // BÀNG: thân thẳng, tán xếp tầng nằm ngang như chiếc ô nhiều lớp, điểm lá đỏ vàng mùa đông
  createTerminaliaTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_Bang_Voxel';
    const rnd = createRng(options.seed ?? 23);
    this._treePit(tree, 1.4, rnd);
    this._trunk(tree, { height: 4.6, baseW: 0.32, topW: 0.2, segments: 5, color: '#4a3526' });
    const bark = this.mat('#4a3526', { roughness: 0.95 });
    const tiers = [
      { y: 2.2, r: 1.9 },
      { y: 3.1, r: 1.55 },
      { y: 3.9, r: 1.15 },
      { y: 4.6, r: 0.7 }
    ];
    tiers.forEach((t, i) => {
      [0, Math.PI / 2, Math.PI, Math.PI * 1.5].forEach((a) => {
        const ang = a + i * 0.4;
        this._limb(
          tree,
          new THREE.Vector3(0, t.y - 0.15, 0),
          new THREE.Vector3(Math.cos(ang) * t.r * 0.8, t.y - 0.05, Math.sin(ang) * t.r * 0.8),
          0.09,
          bark,
          false
        );
      });
      this._canopy(
        tree,
        {
          x: 0,
          y: t.y,
          z: 0,
          rx: t.r,
          ry: 0.22,
          rz: t.r,
          palette: ['#166534', '#15803d', '#16a34a', '#14532d'],
          count: Math.round(10 + t.r * 9),
          accent: ['#dc2626', '#ea580c', '#facc15'],
          accentRatio: 0.08
        },
        rnd
      );
    });
    return tree;
  }

  // DỪA: thân cong nhiều đốt khoanh, 8 tàu lá dài rủ vòng cung, chùm dừa xanh
  createPalmTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_Dua_Voxel';
    const rnd = createRng(options.seed ?? 37);
    this._treePit(tree, 1.2, rnd);
    const bark = this.mat('#8b6b4a', { roughness: 0.95 });
    const barkDark = this.mat('#6f5236', { roughness: 0.95 });
    let pos = new THREE.Vector3(0, 0.1, 0);
    const segs = 10;
    for (let i = 0; i < segs; i++) {
      const bend = 0.04 + i * 0.022;
      const next = new THREE.Vector3(pos.x + bend, pos.y + 0.46, pos.z);
      this._limb(tree, pos, next, 0.3 - i * 0.01, i % 2 ? barkDark : bark);
      pos = next;
    }
    const top = pos;
    const leafColors = ['#16a34a', '#15803d', '#22c55e'];
    for (let f = 0; f < 8; f++) {
      const a = (f / 8) * Math.PI * 2 + rnd() * 0.3;
      const dir = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
      for (let k = 0; k < 6; k++) {
        const d = 0.25 + k * 0.3;
        const width = 0.46 - k * 0.055;
        // Khúc lá dài hơn bước (0.3) để các khúc gối lên nhau thành tàu lá liền mạch
        const leaf = this.box(
          tree,
          0.44,
          0.05,
          width,
          this.mat(k === 5 ? '#a3e635' : leafColors[(f + k) % leafColors.length], { roughness: 0.8 }),
          top.x + dir.x * d,
          top.y + 0.25 - k * k * 0.045,
          top.z + dir.z * d
        );
        leaf.rotation.set(0, -a, -0.1 - k * 0.12);
      }
    }
    const nut = this.mat('#65a30d', { roughness: 0.8 });
    [
      [0.14, 0.1],
      [-0.14, 0.1],
      [0.1, -0.14],
      [-0.1, -0.12],
      [0, 0.16]
    ].forEach(([nx, nz]) => {
      this.box(tree, 0.2, 0.22, 0.2, nut, top.x + nx, top.y - 0.12, top.z + nz);
    });
    return tree;
  }

  // HOA SỮA (Hà Nội): thân xám cao thẳng, tán 2 tầng xanh thẫm điểm chùm hoa trắng kem
  createMilkwoodTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_HoaSua_Voxel';
    const rnd = createRng(options.seed ?? 51);
    this._treePit(tree, 1.4, rnd);
    const top = this._trunk(tree, { height: 3.0, baseW: 0.34, topW: 0.24, segments: 4, color: '#78716c' });
    const bark = this.mat('#78716c', { roughness: 0.95 });
    [
      [0.8, 0.5, 0.2],
      [-0.7, 0.6, -0.3],
      [0.1, 0.6, -0.8]
    ].forEach(([dx, dy, dz]) => {
      this._limb(
        tree,
        new THREE.Vector3(top.x, top.y - 0.4, top.z),
        new THREE.Vector3(top.x + dx, top.y + dy - 0.2, top.z + dz),
        0.12,
        bark
      );
    });
    const palette = ['#14532d', '#166534', '#15803d', '#1a4d2e'];
    const flowers = ['#fefce8', '#fef9c3', '#f5f5dc'];
    this._canopy(
      tree,
      {
        x: top.x,
        y: top.y + 0.5,
        z: top.z,
        rx: 1.8,
        ry: 0.85,
        rz: 1.7,
        palette,
        count: 60,
        accent: flowers,
        accentRatio: 0.22
      },
      rnd
    );
    this._canopy(
      tree,
      {
        x: top.x,
        y: top.y + 1.6,
        z: top.z,
        rx: 1.15,
        ry: 0.75,
        rz: 1.1,
        palette,
        count: 34,
        accent: flowers,
        accentRatio: 0.3
      },
      rnd
    );
    return tree;
  }

  // BẰNG LĂNG: thân nhẵn màu xám be tách đôi, tán tròn phủ chùm hoa tím
  createLagerstroemiaTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_BangLang_Voxel';
    const rnd = createRng(options.seed ?? 63);
    this._treePit(tree, 1.3, rnd);
    const top = this._trunk(tree, { height: 1.4, baseW: 0.26, topW: 0.22, segments: 2, color: '#b8a898' });
    const bark = this.mat('#b8a898', { roughness: 0.7 });
    const forks = [new THREE.Vector3(0.55, 2.6, 0.2), new THREE.Vector3(-0.45, 2.7, -0.25)];
    forks.forEach((f) => this._limb(tree, top, f, 0.16, bark));
    this._canopy(
      tree,
      {
        x: 0,
        y: 3.2,
        z: 0,
        rx: 1.55,
        ry: 1.05,
        rz: 1.5,
        palette: ['#15803d', '#16a34a', '#166534'],
        count: 70,
        accent: ['#a855f7', '#9333ea', '#c084fc', '#7e22ce'],
        accentRatio: 0.5
      },
      rnd
    );
    return tree;
  }
}

export function registerTreesTropicalMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
